import { GetServerSidePropsContext, NextPageContext } from 'next';
import { KnownCookies } from '@sls-react/typings/cookies';
import { DecodedJwt } from '@sls-react/typings/services/iam/auth';
import { parseCookies } from 'nookies';
import qsStringify from '@sls-react/utils/qsStringify';
import decodeJwt from '@sls-react/utils/decodeJwt';
import getBaseUrl from '@sls-react/api/utils/getBaseUrl';
import {
  sessionContextLogout,
  setAccessToken,
  setRefreshToken,
} from '@sls-react/lib/session/nextContextSessionManager';
import { fetchNewAccessToken } from '@sls-react/api/iam/auth/sideline';
import { ApiError, AuthRefreshFailure } from '@sls-react/api/ApiError';
import express from 'express';
import { Logger } from 'pino';

const maxAuthRetryAttempts = 5;

type Arr = readonly unknown[];

// Progress on fixing the typing of this function:

type InferArguments<T> = T extends (...t: [...infer R]) => any ? R : never;

type RDescriptor =
  | FetcherEndpoint
  | [endpoint: FetcherEndpoint, config?: FetcherOptions];
type OptionsFn = (...args: any[]) => RDescriptor;

export function makeFetcher<
  TResult,
  Arguments extends readonly any[] = readonly any[],
>(options: OptionsFn): (...args: Arguments) => Promise<TResult> {
  return function (this: NextPageContext | void, ...args: Arguments) {
    const result = options(...args);
    return fetcher.apply<any, any, Promise<TResult>>(
      this,
      Array.isArray(result) ? result : [result],
    );
  };
}

export interface FetcherOptions {
  abortController?: AbortController;
  authHeader?: boolean;
  accessToken?: string;
  baseUrl?: string;
  dataOnly?: false;
  blob?: boolean;
  method?: 'get' | 'post' | 'put' | 'delete';
  body?: any;
  ignoreErrors?: boolean;
  headers?: Record<string, any>;
  params?: Record<string, any>;
  skipRefresh?: boolean;

  authRetryAttempt?: number;
}

const defaultOptions: Partial<Omit<FetcherOptions, 'dataOnly'>> & {
  dataOnly: boolean;
} = {
  authHeader: true,
  dataOnly: true,
  baseUrl: getBaseUrl(),
  method: 'get',
  authRetryAttempt: 0,
};

export type FetcherEndpoint =
  | ((jwt: Partial<DecodedJwt>) => string | false)
  | string;

export default async function fetcher<R>(
  // eslint-disable-next-line @typescript-eslint/ban-types
  this: void | object | NextPageContext | GetServerSidePropsContext,
  endpoint: FetcherEndpoint,
  config?: FetcherOptions | null,
): Promise<R> {
  const options = { ...defaultOptions, ...config };
  const req: express.Request = this?.['req'];

  let cookies: KnownCookies = {};

  if (typeof document === 'undefined') {
    if (req) {
      if (req['cookies']) {
        cookies = req['cookies'];
      } else {
        cookies = parseCookies(this as NextPageContext);
      }
    }
  } else {
    cookies = parseCookies();
  }

  const accessToken =
    options.accessToken ||
    (this as NextPageContext)?.session?.accessToken ||
    cookies.access_token;
  let validRefreshToken: boolean;

  const evaluatedEndpoint =
    typeof endpoint === 'function'
      ? endpoint(decodeJwt(accessToken) || {})
      : endpoint;

  // If the endpoint function returned "false", bail out of fetching.
  // This is a way to stop fetching if you're logged out
  if (!evaluatedEndpoint) {
    return null as unknown as R;
  }

  const headers = {
    ...options.headers,
    'x-sls-client-type': 'web',
  };

  // No content-type and not a get request, add content type and serialize body
  if (
    typeof headers['content-type'] === 'undefined' &&
    options.method !== 'get'
  ) {
    headers['content-type'] = 'application/json';
  }

  if (headers['content-type'] === null) {
    delete headers['content-type'];
  }

  if (options.authHeader != false && accessToken) {
    headers['authorization'] = `Bearer ${accessToken}`;
  }

  if (req && req.connection?.remoteAddress) {
    const newForwarded = (req.header('x-forwarded-for') || '').split(',');
    newForwarded.push(req.connection.remoteAddress);
    headers['x-forwarded-for'] = newForwarded.join(',');
  }

  if (cookies.sls_client_id) {
    headers['x-session-id'] = cookies.sls_client_id;
  }

  const stringifiedParams = qsStringify(options.params);
  const url = options.baseUrl + evaluatedEndpoint + stringifiedParams;
  const params: RequestInit = {
    headers,
    body:
      options.body && headers['content-type'] === 'application/json'
        ? JSON.stringify(options.body)
        : options.body,
    credentials: 'same-origin',
    // mode: 'cors',
    method: options.method,
  };

  if (options.abortController?.signal) {
    params.signal = options.abortController.signal;
  }

  if (typeof window !== 'undefined') {
    window.Sentry?.addBreadcrumb({
      message: 'Calling `fetch` with these params',
      category: 'fetcher',
      level: 'debug',
      data: {
        url,
        params,
      },
    });
  }

  const response = await fetch(url, params);

  if (response.ok) {
    if (options.blob) {
      // @ts-ignore
      return response.blob();
    }
    const content = await response.text();
    if (content) {
      const body = JSON.parse(content);
      if (options.dataOnly && body?.hasOwnProperty('data')) {
        return body?.data;
      }
      return body;
    }

    // @ts-ignore
    return null;
  }

  const refreshToken =
    (this as NextPageContext)?.session?.refreshToken || cookies.refresh_token;

  if (refreshToken) {
    validRefreshToken = !!decodeJwt(refreshToken);
  } else {
    validRefreshToken = false;
  }

  if (
    response.status === 401 &&
    !options.skipRefresh &&
    options.authRetryAttempt! < maxAuthRetryAttempts &&
    validRefreshToken
  ) {
    try {
      const tokenResponse = await fetchNewAccessToken.call(
        this as NextPageContext,
        refreshToken,
        cookies.sls_client_id,
      );

      setAccessToken(this as NextPageContext, tokenResponse.access_token);
      setRefreshToken(this as NextPageContext, tokenResponse.refresh_token);
    } catch (e) {
      setAccessToken(this as NextPageContext, undefined);
      setRefreshToken(this as NextPageContext, undefined);

      if (options.ignoreErrors) {
        // @ts-ignore
        return null;
      } else {
        throw AuthRefreshFailure.fromApiError(e as ApiError);
      }
    }

    // Replay the original request now that cookies have been changed
    return fetcher.call<any, Parameters<typeof fetcher>, Promise<R>>(
      this,
      endpoint,
      // @ts-ignore
      {
        ...options,
        authRetryAttempt: options.authRetryAttempt
          ? options.authRetryAttempt + 1
          : 1,
      },
    );
  }

  // 401s are handled in the previous block, but this block is for
  // if token refreshing returns a 401
  if (response.status === 401) {
    setAccessToken(this as NextPageContext, undefined);
    setRefreshToken(this as NextPageContext, undefined);

    const shouldRefetch = (this as NextPageContext)?.session?.shouldRefetch;

    await sessionContextLogout(this as NextPageContext, shouldRefetch);

    if (shouldRefetch && options.authRetryAttempt! < maxAuthRetryAttempts) {
      return fetcher.call<any, Parameters<typeof fetcher>, Promise<R>>(
        this,
        endpoint,
        // @ts-ignore
        {
          ...options,
          authRetryAttempt: options.authRetryAttempt
            ? options.authRetryAttempt + 1
            : 1,
        },
      );
    }
  }

  if (options.ignoreErrors) {
    // @ts-ignore
    return null;
  } else {
    const content = await response.text();
    throw ApiError.fromResponse(response, content);
  }
}
